import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { daysLabel } from "../lib/format";
import { UserCircle, Crown, LogOut, Calendar, Mail, ShieldCheck } from "lucide-react";

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [intake, setIntake] = useState(null);
  const [busy, setBusy] = useState(false);

  const cancel = async () => {
    if (!window.confirm("Cancel auto-renew? You'll keep full access until your current period ends.")) return;
    setBusy(true);
    try { await api.post("/subscription/cancel"); await refresh(); toast.success("Auto-renew cancelled — access stays until period end"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true);
    try { await api.post("/subscription/resume"); await refresh(); toast.success("Auto-renew resumed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => { api.get("/intake").then((r) => setIntake(r.data)).catch(() => {}); }, []);

  const expires = user?.access_expires_at ? new Date(user.access_expires_at).toLocaleDateString() : null;
  const daysLeft = user?.access_expires_at ? Math.max(0, Math.ceil((new Date(user.access_expires_at) - new Date()) / 86400000)) : null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <UserCircle className="h-8 w-8 text-amber-600" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">My Account</h1>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="account-info">
          <div className="flex items-center gap-3 text-sm"><Mail className="h-4 w-4 text-slate-400" /><span className="text-slate-500 w-24">Email</span><span className="font-medium text-[#0A192F]">{user?.email}</span></div>
          <div className="flex items-center gap-3 text-sm"><UserCircle className="h-4 w-4 text-slate-400" /><span className="text-slate-500 w-24">Name</span><span className="font-medium text-[#0A192F]">{user?.name}</span></div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-6">
          <h3 className="font-heading font-semibold text-[#0A192F] mb-4 flex items-center gap-2"><Crown className="h-5 w-5 text-amber-600" /> Access Status</h3>
          {user?.has_full_access ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              {user?.access_type === "lifetime" ? (
                <>
                  <div className="font-semibold text-emerald-700 flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Lifetime Access</div>
                  <p className="text-sm text-emerald-700/80 mt-1">You have complimentary full access — no renewal needed.</p>
                </>
              ) : user?.cancel_at_period_end ? (
                <>
                  <div className="font-semibold text-emerald-700 flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Full Access Active</div>
                  <p className="text-sm text-emerald-700/80 mt-1 flex items-center gap-1"><Calendar className="h-4 w-4" /> Access until {expires} · won't renew</p>
                  <Button size="sm" variant="outline" className="mt-4 border-emerald-300 text-emerald-700" onClick={resume} disabled={busy} data-testid="account-resume-btn">Resume auto-renew</Button>
                </>
              ) : (
                <>
                  <div className="font-semibold text-emerald-700 flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Full Access Active</div>
                  <p className="text-sm text-emerald-700/80 mt-1 flex items-center gap-1"><Calendar className="h-4 w-4" /> Renews {expires} · $49.95/yr</p>
                  <Button size="sm" variant="outline" className="mt-4 border-red-200 text-red-600 hover:bg-red-50" onClick={cancel} disabled={busy} data-testid="account-cancel-btn">Cancel subscription</Button>
                </>
              )}
            </div>
          ) : (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
              <div className="font-semibold text-amber-800">Free Account</div>
              <p className="text-sm text-amber-700/80 mt-1">Sample content + 10 practice questions/day. Subscribe for full access.</p>
              <Button size="sm" className="mt-4 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate("/upgrade")} data-testid="account-upgrade-btn">Subscribe — $49.95/yr</Button>
            </div>
          )}
        </div>

        {intake && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 mt-6" data-testid="account-intake">
            <h3 className="font-heading font-semibold text-[#0A192F] mb-4">Study Profile</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-500 block">State</span><span className="font-medium text-[#0A192F]">{intake.state}</span></div>
              <div><span className="text-slate-500 block">License</span><span className="font-medium text-[#0A192F]">{intake.license_type}</span></div>
              <div><span className="text-slate-500 block">Exam Date</span><span className="font-medium text-[#0A192F]">{intake.exam_date || "Not set"}</span></div>
              <div><span className="text-slate-500 block">Daily Goal</span><span className="font-medium text-[#0A192F]">{intake.daily_goal_minutes} min</span></div>
            </div>
            <Button size="sm" variant="ghost" className="mt-4" onClick={() => navigate("/intake")}>Update profile</Button>
          </div>
        )}

        <Button variant="outline" className="w-full mt-6 text-red-600 border-red-200 hover:bg-red-50" onClick={() => { logout(); navigate("/"); }} data-testid="account-logout-btn">
          <LogOut className="h-4 w-4 mr-2" /> Log out
        </Button>
      </div>
    </Layout>
  );
}
